import { Controller } from "@hotwired/stimulus"

const SAVE_DELAY_MS = 500

// Connects to data-controller="cart"
export default class extends Controller {
  static targets = ["quantity", "subtotal", "productsTotal", "installationTotal", "totalWeight", "deliveryFee", "total"]

  connect() {
    this.recalculate()
  }

  increment(event) {
    const input = this.rowInputFor(event.currentTarget)
    input.value = this.clamp(input, this.valueOf(input) + 1)
    this.recalculate()
    this.scheduleSave(input)
  }

  decrement(event) {
    const input = this.rowInputFor(event.currentTarget)
    input.value = this.clamp(input, this.valueOf(input) - 1)
    this.recalculate()
    this.scheduleSave(input)
  }

  onQuantityInput(event) {
    this.recalculate()
    this.scheduleSave(event.target)
  }

  // Debounced so rapid +/- clicks or typing coalesce into a single save,
  // instead of firing a request (and full-page Turbo swap) on every change.
  scheduleSave(input) {
    clearTimeout(input.saveTimeout)
    input.saveTimeout = setTimeout(() => {
      input.closest("form").requestSubmit()
    }, SAVE_DELAY_MS)
  }

  recalculate() {
    let productsTotalCents = 0
    let installationTotalCents = 0
    let totalWeightKg = 0

    this.quantityTargets.forEach((input, index) => {
      input.value = this.clamp(input, this.valueOf(input))
      const quantity = this.valueOf(input)
      const productCents = this.unitPriceCentsOf(input) * quantity
      const installationCents = this.installationPriceCentsOf(input) * quantity
      this.subtotalTargets[index].textContent = this.formatMoney(productCents + installationCents)
      productsTotalCents += productCents
      installationTotalCents += installationCents
      totalWeightKg += this.weightKgOf(input) * quantity
    })

    if (this.hasProductsTotalTarget) {
      this.productsTotalTarget.textContent = this.formatMoney(productsTotalCents)
    }

    if (this.hasInstallationTotalTarget) {
      this.installationTotalTarget.textContent = this.formatMoney(installationTotalCents)
    }

    if (this.hasTotalWeightTarget) {
      this.totalWeightTarget.textContent = this.formatWeight(totalWeightKg)
    }

    const deliveryFeeCents = Math.round(totalWeightKg * this.venipakRateCentsPerKg())
    this.deliveryFeeTargets.forEach((el) => { el.textContent = this.formatMoney(deliveryFeeCents) })

    this.totalTarget.textContent = this.formatMoney(productsTotalCents + installationTotalCents + deliveryFeeCents)
  }

  venipakRateCentsPerKg() {
    return parseInt(this.element.dataset.cartVenipakRateCentsPerKg, 10) || 0
  }

  rowInputFor(element) {
    return element.closest("[data-cart-row]").querySelector('[data-cart-target="quantity"]')
  }

  valueOf(input) {
    return parseInt(input.value, 10) || 1
  }

  unitPriceCentsOf(input) {
    return parseInt(input.dataset.unitPriceCents, 10) || 0
  }

  installationPriceCentsOf(input) {
    return parseInt(input.dataset.installationPriceCents, 10) || 0
  }

  weightKgOf(input) {
    return parseFloat(input.dataset.weightKg) || 0
  }

  clamp(input, quantity) {
    const max = parseInt(input.dataset.max, 10) || 1
    return Math.min(Math.max(quantity, 1), max)
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace(".", ",")
    return `€${euros}`
  }

  // Mirrors number_with_precision(..., strip_insignificant_zeros: true) used
  // for the server-rendered initial value, so neither jumps on first render.
  formatWeight(kg) {
    const rounded = Math.round(kg * 100) / 100
    const trimmed = parseFloat(rounded.toFixed(2)).toString()
    return `${trimmed.replace(".", ",")} kg`
  }
}
